import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import useNotification from 'utils/notification'
import { getUserRole } from 'store/me'
import {
  TradeOfferLotsFiltersDocument,
  TradeOfferLotsSpecificFilterDocument,
  AdminRetailWinesCountersDocument,
  AdminTradeOfferLotsPaginationDocument,
  RetailWinesCountersDocument,
  TradeOfferLotsPaginationDocument,
} from 'api/hooks'
import type { RootState } from 'store'
import {
  getSort,
  getPageInfo,
  setItems,
  setPageInfo,
  getCurrency,
  getRefetchTrigger,
  getWineLotBottleQuantity,
  getDeliveryCostCode,
  getVatBehaviorCode,
  getPurchaseConditionCode,
  getSelectorsInitialized,
} from 'store/retail-wines'
import { getSelectedCompanyId } from 'utils/company'
import { getLocaleId } from 'utils/getLocaleId'
import { useFilterStoreData } from 'utils/hooks/useFilterStoreData'
import { useTableFiltersInputs } from 'utils/hooks/useTableFiltersInputs'
import { MerchantSort, FacetInput } from 'api/schemas'
import {
  useCalculatePageOnPerPageChange,
  usePerPage,
} from 'components/Common/Table/utils'
import { useParams, useLocation } from 'react-router-dom'
import { ManagementRoles } from 'utils/consts/userRoles'
import { useLazyQuery } from '@apollo/client'
import type { RetailWinesTradeOfferLotFragment } from 'api/operations'

const tabsNames = {
  myRange: 'my-range',
  compare: 'compare',
}
export const RetailWinesCurrencyLocalStorage = 'retail-wines-currency'
export const preparePageName = (page: string) => {
  let slicedName = page.substring(3)
  return slicedName.charAt(0).toLowerCase() + slicedName.slice(1)
}
export const DEFAULT_SORT = MerchantSort.RetailCoefAsc
const useGetSub = () => {
  const { sub: subParam } = useParams<{ sub: string }>()
  const location = useLocation()
  return (
    subParam ||
    (location.pathname.includes(tabsNames.myRange) ? tabsNames.myRange : '')
  )
}
export const useGetVariables = () => {
  const dispatch = useAppDispatch()
  const { getTableFilters, filtersFromStorage } = useTableFiltersInputs()
  const { storeName } = useFilterStoreData()
  const { value: perPage } = usePerPage()
  const pageInfo = useAppSelector(getPageInfo)
  const { page = 1 } = pageInfo || {}
  const sub = useGetSub()
  const isCompare = sub === tabsNames.compare

  const query = useAppSelector(
    (state: RootState) =>
      state?.tableFilters?.[storeName]?.filters?.query ?? null
  )

  const facetsInputs = useMemo(
    () => getTableFilters(filtersFromStorage),
    [filtersFromStorage]
  )

  const wineLotBottleQuantity = useAppSelector(getWineLotBottleQuantity)
  const deliveryCostCode = useAppSelector(getDeliveryCostCode)
  const vatBehaviorCode = useAppSelector(getVatBehaviorCode)
  const purchaseConditionCode = useAppSelector(getPurchaseConditionCode)

  const filter = useMemo(
    () => ({
      ...(getSelectedCompanyId()
        ? { companyId: getSelectedCompanyId() }
        : { companyId: null }),
      onlyMyRange: sub === tabsNames.myRange ? true : false,
      onlyCompare: isCompare,
      only1Bottle: null,
      ...(isCompare
        ? { purchaseConditionCode: null }
        : {
            bottleQuantity: wineLotBottleQuantity ?? null,
            deliveryCostCode: deliveryCostCode ?? null,
            vatBehaviorCode: vatBehaviorCode ?? null,
            purchaseConditionCode:
              purchaseConditionCode !== undefined
                ? purchaseConditionCode
                : [null],
          }),
      ...(facetsInputs && facetsInputs.length > 0 ? { facetsInputs } : {}),
    }),
    [
      JSON.stringify(facetsInputs),
      getSelectedCompanyId(),
      sub,
      wineLotBottleQuantity,
      deliveryCostCode,
      vatBehaviorCode,
      purchaseConditionCode,
    ]
  )

  useEffect(() => {
    dispatch(setPageInfo({ ...pageInfo, page: 1 }))
  }, [filter])

  const sort = useAppSelector(getSort)?.type || DEFAULT_SORT
  const retailWines =
    useAppSelector((state: RootState) => state?.retailWines?.items) || []
  const needBackToPrevPage = !retailWines.length && page > 1
  const pageName = preparePageName(storeName)
  const buyerCurrencyCode = useAppSelector(getCurrency)?.code || null
  const merchantFilter = useMemo(() => {
    const { companyId: _c, onlyMyRange: _r, onlyCompare: _o, ...rest } = filter
    return {
      ...rest,
      localeId: getLocaleId(),
      ...(buyerCurrencyCode && { currencyCode: buyerCurrencyCode }),
    }
  }, [filter, buyerCurrencyCode])

  return useMemo(
    () => ({
      localeId: getLocaleId(),
      page,
      ...(getSelectedCompanyId()
        ? { companyId: getSelectedCompanyId() }
        : { companyId: null }),
      ...(query && { query }),
      ...(sort && { sort }),
      filter,
      merchantFilter,
      perPage,
      pageName,
      buyerCurrencyCode,
    }),
    [
      needBackToPrevPage,
      page,
      sort,
      query,
      filter,
      merchantFilter,
      perPage,
      getSelectedCompanyId(),
      buyerCurrencyCode,
    ]
  )
}

export const useSelectorsReady = () => {
  const sub = useGetSub()
  const isCompare = sub === tabsNames.compare
  const initialized = useAppSelector(getSelectorsInitialized)
  return isCompare || initialized
}

const getFilterVariables = ({
  page,
  perPage = 50,
  query = '',
  filterKey = '',
  facetsInputs = null,
  filterSearch,
  sub,
  bottleQuantity = null,
  deliveryCostCode = null,
  vatBehaviorCode = null,
  purchaseConditionCode = null,
}: {
  page?: number
  perPage?: number
  filterKey?: string
  facetsInputs?: FacetInput[] | null
  filterSearch?: string
  query?: string | null
  sub: string
  bottleQuantity?: number[] | null
  deliveryCostCode?: string[] | null
  vatBehaviorCode?: string[] | null
  purchaseConditionCode?: Array<string | null> | null
}) => {
  const isCompare = sub === tabsNames.compare
  return {
    localeId: getLocaleId(),
    page,
    companyId: getSelectedCompanyId(),
    perPage,
    filter: {
      companyId: getSelectedCompanyId(),
      onlyMyRange: sub === tabsNames.myRange ? true : false,
      onlyCompare: isCompare,
      only1Bottle: null,
      ...(isCompare
        ? { purchaseConditionCode: null }
        : {
            bottleQuantity,
            deliveryCostCode,
            vatBehaviorCode,
            purchaseConditionCode:
              purchaseConditionCode !== undefined
                ? purchaseConditionCode
                : [null],
          }),
      ...(facetsInputs && facetsInputs.length > 0 && { facetsInputs }),
    },
    ...(query && { query }),
    ...(filterKey && { filterKey: filterKey }),
    ...(filterSearch && { filterSearch }),
  }
}

export const useTableData = () => {
  const dispatch = useAppDispatch()
  const notify = useNotification()
  const sub = useGetSub()
  const { storeName, setPresetActionType } = useFilterStoreData()
  const requestVariables = useGetVariables()
  const selectorsReady = useSelectorsReady()
  const calculatePage = useCalculatePageOnPerPageChange()

  const isAdmin = useIsAdmin()

  const { value: perPage } = usePerPage()
  const presetInStore = useAppSelector(
    (state: RootState) => state?.tableFilters?.[storeName]?.preset ?? null
  )
  const pageInfo = useAppSelector(getPageInfo)
  const query = useAppSelector(
    (state: RootState) =>
      state?.tableFilters?.[storeName]?.filters?.query ?? null
  )
  const getFiltersApplied = (storeName: string) => (state: RootState) =>
    state?.tableFilters?.[storeName]?.filtersApplied ?? false
  const filtersApplied = useAppSelector(getFiltersApplied(storeName))
  const refetchTrigger = useAppSelector(getRefetchTrigger)

  const [loading, setLoading] = useState(false)
  const requestIdRef = useRef(0)

  const [getTableData, { loading: queryLoading, error, data }] = useLazyQuery(
    isAdmin
      ? AdminTradeOfferLotsPaginationDocument
      : TradeOfferLotsPaginationDocument,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  )

  const requestTableData = useCallback(
    async (vars: any = {}) => {
      const currentRequestId = ++requestIdRef.current
      setLoading(true)

      try {
        const result = await getTableData({
          variables: {
            ...requestVariables,
            ...(vars
              ? vars?.filter
                ? {
                    filter: {
                      facetsInputs: vars?.filter,
                      ...requestVariables.filter,
                    },
                  }
                : vars
              : vars),
          },
        })

        if (currentRequestId !== requestIdRef.current) return

        if (result?.data?.tradeOfferLotsPagination) {
          const response = result.data.tradeOfferLotsPagination
          dispatch(setItems(response?.items))
          dispatch(setPageInfo(response?.pageInfo))
        }
      } catch (error: any) {
        if (currentRequestId !== requestIdRef.current) return
        notify(error.graphQLErrors?.[0]?.message ?? 'error', 'error')
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    [requestVariables]
  )

  useEffect(() => {
    if (!selectorsReady) return
    const page = calculatePage(pageInfo?.page)
    requestTableData({ page, perPage })
    dispatch(setPageInfo({ ...pageInfo, page }))
  }, [perPage])

  useEffect(() => {
    if (!selectorsReady) return
    requestTableData({})
  }, [filtersApplied, requestVariables, selectorsReady])

  useEffect(() => {
    if (!selectorsReady) return
    if (refetchTrigger > 0) {
      requestTableData({})
    }
  }, [refetchTrigger])

  const filterQueryData = {
    getFiltersData: TradeOfferLotsFiltersDocument,
    getVariables: variables =>
      getFilterVariables({
        ...variables,
        query,
        sub,
        bottleQuantity: requestVariables.filter.bottleQuantity ?? null,
        deliveryCostCode: requestVariables.filter.deliveryCostCode ?? null,
        vatBehaviorCode: requestVariables.filter.vatBehaviorCode ?? null,
        purchaseConditionCode:
          requestVariables.filter.purchaseConditionCode ?? null,
      }),
    getSpecificFilterData: TradeOfferLotsSpecificFilterDocument,
    specificFilterDataPath: [
      'tradeOfferLotsFilters',
      'filterByKey',
      'valuesPagination',
    ],
  }

  useEffect(() => {
    setLoading(queryLoading)
  }, [queryLoading])

  useEffect(() => {
    if (presetInStore && !filtersApplied) {
      dispatch({
        type: setPresetActionType,
        payload: null,
      })
    }
  }, [])

  return {
    page: requestVariables?.page,
    loading: !data || loading,
    error,
    filterQueryData,
    requestTableData,
  }
}

export const useGetCounters = () => {
  const notify = useNotification()
  const isAdmin = useIsAdmin()

  const [countMyRange, setCountMyRange] = useState(0)
  const [countCompare, setCountCompare] = useState(0)
  const requestVariables = useGetVariables()
  const [getMyRangeData, { data }] = useLazyQuery(
    isAdmin ? AdminRetailWinesCountersDocument : RetailWinesCountersDocument,
    {
      fetchPolicy: 'no-cache',
      onError(error) {
        notify(error.graphQLErrors?.[0]?.message ?? 'error', 'error')
      },
    }
  )

  useEffect(() => {
    if (data) {
      setCountMyRange(data?.myRangeList?.pageInfo?.totalItems ?? 0)
      setCountCompare(data?.compareList?.pageInfo?.totalItems ?? 0)
    }
  }, [data])

  const getCounters = async () => {
    await getMyRangeData({
      variables: {
        ...requestVariables,
      },
    })
  }

  return {
    getCounters,
    countMyRange,
    countCompare,
  }
}

export const checkIsAdmin = (role: string) => {
  return [
    ManagementRoles.SUPERADMIN,
    ManagementRoles.CONTENT,
    ManagementRoles.MANAGER,
  ].includes(role)
}

export const useIsAdmin = () => {
  const role = useAppSelector(getUserRole)

  return checkIsAdmin(role)
}

export const isDefaultCategory = (row?: RetailWinesTradeOfferLotFragment) => {
  if (!row) {
    return false
  }
  return (
    row.lot.bottleQuantity == 1 &&
    row.deliveryCost.code === 'EXCLUDED' &&
    row.vatBehavior.code === 'INCLUDED'
  )
}
